import { Tooltip } from "@voicely/ui-mui/data-display/Tooltip";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { ArrowBackIcon } from "@voicely/ui-mui/icons/ArrowBackIcon";
import { Box } from "@voicely/ui-mui/layout/Box";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { IconButton } from "@voicely/ui-mui/navigation/IconButton";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";

/** Height of the sticky page header, in px. Unexported here, unlike the
 * frontend's copy — nothing else in this app measures against it. */
const PAGE_HEADER_HEIGHT = 60;

interface PageShellProps {
	/** The page's name. Rendered in the sticky header. */
	title: ReactNode;
	/** One line of context under the title. Optional — most pages need none. */
	subtitle?: ReactNode;
	/** Primary actions for the page, right-aligned in the header. */
	actions?: ReactNode;
	/**
	 * Where "back" goes, rendered before the title.
	 *
	 * Left, not right. Back is a navigation, and every other back in every
	 * browser, phone and app sits at the leading edge — putting it beside Save
	 * and Delete both hides it (nobody looks right for it) and files it with
	 * the page's actions, which it is not. A string is a destination; pass
	 * `true` to step back through history instead, for a page reachable from
	 * more than one place.
	 */
	backTo?: string | true;
	/** Overrides the back control's tooltip and accessible name. */
	backLabel?: string;
	/**
	 * An avatar or icon for the thing this page is about, drawn before the
	 * title. Exists so a page whose subject has an identity — a team, an agent
	 * — can show it in the header instead of repeating the whole identity in a
	 * card at the top of the body, directly under the name it just gave.
	 */
	avatar?: ReactNode;
	/** Rendered between the header and the content — filters, tabs, a search box. */
	toolbar?: ReactNode;
	/**
	 * Caps the content column. `false` lets it run the full width, which is what
	 * a wide table wants and what a settings form very much does not: a form
	 * whose fields are 1800px wide is unreadable, because the eye loses the row
	 * between the label and the input.
	 */
	maxWidth?: number | false;
	/** Removes the content padding, for pages that manage their own gutters. */
	disableGutters?: boolean;
	children: ReactNode;
}

/**
 * The frame every authenticated page is drawn in.
 *
 * ## Why a component and not a layout route
 *
 * The header needs the page's title and its actions, and a layout route sits
 * *above* the page and cannot see either without a registry mapping paths to
 * titles — a second place to edit whenever a route is added, and one that goes
 * stale silently because nothing renders differently when it is wrong. Having
 * the page pass its own title keeps the two together.
 *
 * ## Why the header is sticky
 *
 * The lists in this product are long — a busy workspace's call history runs to
 * hundreds of rows — and a title that scrolls away takes the page's primary
 * action with it. Keeping the bar pinned means "New Session" is reachable from
 * row 300 without scrolling back up.
 *
 * On mobile it is also the *only* header: the shell's own top bar is gone and
 * its navigation moved to the bottom bar and its drawer, so this sticks to the
 * top of the viewport at every width.
 */
export const PageShell = ({
	title,
	subtitle,
	actions,
	backTo,
	backLabel = "Back",
	avatar,
	toolbar,
	maxWidth = 1200,
	disableGutters = false,
	children,
}: PageShellProps) => {
	const navigate = useNavigate();

	return (
		<>
			<Box
				component="header"
				sx={{
					position: "sticky",
					top: 0,
					zIndex: (t) => t.zIndex.appBar - 1,
					bgcolor: "surface.chrome",
					borderBottom: "1px solid",
					borderColor: "surface.border",
					px: { xs: 2, sm: 3, md: 4 },
				}}
			>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					spacing={2}
					sx={{
						minHeight: PAGE_HEADER_HEIGHT,
						py: 1,
						maxWidth: maxWidth || "none",
						mx: "auto",
					}}
				>
					<Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
						{backTo !== undefined && (
							<Tooltip title={backLabel}>
								<IconButton
									aria-label={backLabel}
									onClick={() => (backTo === true ? navigate(-1) : navigate(backTo))}
									size="small"
									sx={{
										flexShrink: 0,
										border: "1px solid",
										borderColor: "surface.border",
										color: "text.secondary",
										"&:hover": { color: "text.primary", borderColor: "surface.borderStrong" },
									}}
								>
									<ArrowBackIcon sx={{ fontSize: 18 }} />
								</IconButton>
							</Tooltip>
						)}
						{avatar && <Box sx={{ flexShrink: 0, display: "flex" }}>{avatar}</Box>}
						<Box sx={{ minWidth: 0 }}>
							<Typography variant="h4" component="h1" noWrap>
								{title}
							</Typography>
							{subtitle && (
								<Typography variant="body2" color="text.secondary" noWrap>
									{subtitle}
								</Typography>
							)}
						</Box>
					</Stack>
					{actions && (
						<Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
							{actions}
						</Stack>
					)}
				</Stack>

				{toolbar && <Box sx={{ maxWidth: maxWidth || "none", mx: "auto", pb: 1.5 }}>{toolbar}</Box>}
			</Box>

			<Box
				sx={{
					flexGrow: 1,
					px: disableGutters ? 0 : { xs: 2, sm: 3, md: 4 },
					py: disableGutters ? 0 : { xs: 2.5, md: 3.5 },
				}}
			>
				<Box sx={{ maxWidth: maxWidth || "none", mx: "auto" }}>{children}</Box>
			</Box>
		</>
	);
};
